import type { ItemCheck, VoiceCheck } from '../reach/voiceWords';
import { useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';
import { useAccount } from '../../context/AccountContext';
import { GET_TINYCMS_TODAY } from '../../graphql';
import { useAddons } from './addons';
import type { TodayMenuLinks } from './navChecks';
import type { Finding } from './shared';
import { postTime, REACH_CALENDAR, ReachPost } from '../reach/reach';

/**
 * What Today shows, shared with the header's Today badge so the two always
 * agree (and Apollo sends each request once): tinycmsToday for the window,
 * Reach's calendar for the last seven days (its `waiting` list ignores the
 * window), and campaigns with posts in review.
 */

export type TodayKind = 'article' | 'page' | 'form' | 'site';

export interface TodayWaiting {
  kind: TodayKind;
  id: string;
  title: string | null;
  path: string | null;
  note: string | null;
  heroUrl: string | null;
  live: boolean;
  updatedAt: string | null;
  workingHash: string;
  checks: TodayChecks | null;
  /**
   * The TodayGroup it goes live with: pages, the waiting forms they place, the
   * waiting pages they link to, and the site's look and navigation when its
   * menus or home page point at them.
   */
  group?: string;
  /** Pages: the account requires a copy edit and this version hasn't had one. */
  copyEditPending?: boolean;
  /** Pages: the waiting forms it places. */
  needs?: { id: string; title: string | null }[];
  /** Who proposed this version: a person, a feed or an agent (feedback 9f70af88). */
  proposedBy?: { kind: 'person' | 'feed' | 'agent'; name: string | null } | null;
}

export interface TodayChecks {
  blocking: number;
  /** Only an owner or admin can approve it anyway. */
  held?: number;
  warnings: number;
  first: string | null;
  more: number;
  /** Copy-edit wording suggestions: optional, never a problem (backlog adaf66b4). */
  suggestions?: number;
}

/** Waiting items that go live together; checks as if published together. */
export interface TodayGroup {
  id: string;
  pages: string[];
  forms: string[];
  /** The site's look and navigation goes with them (its links point at these pages). */
  site?: boolean;
  checks: TodayChecks;
  /** With the site: its own findings as if the group were live (Design's publish checks). */
  siteFindings?: Finding[];
  /** With the site: the pages' and forms' checks alone. */
  otherChecks?: TodayChecks;
}

export interface TodayScheduled {
  kind: TodayKind | 'dataset';
  id: string;
  title: string | null;
  path: string | null;
  at: string;
  cat: string | null;
}

export interface TodayApproval {
  id: string;
  kind: string;
  subjectId: string;
  action: 'publish' | 'schedule' | 'unpublish';
  approvedBy?: string;
  /** One name for the person ("Hai"), from the server. */
  approverName?: string | null;
  via?: 'ui' | 'mcp';
  subjectTitle?: string | null;
  subjectPath?: string | null;
  /** A form's place: the live pages that show it (forms have no address of their own). */
  placedOn?: string[];
  versionNumber?: number | null;
  scheduledFor?: string | null;
  recordedAt: string;
  stillLive: boolean;
}

/** New form responses in the window, per form (Today's "N new form responses"). */
export interface TodayResponses {
  formId: string;
  name: string;
  purpose?: 'contact' | 'signup';
  count: number;
  emailed: boolean;
}

/** Rows in the last 7 days and in the 7 before. */
export interface WeekCount {
  count: number;
  previous: number;
}

/** The site's Google Analytics connection, as the app sees it (never a token). */
export interface AnalyticsConnection {
  provider: 'google';
  propertyId: string;
  propertyName: string | null;
  accountName: string | null;
  measurementId: string | null;
  email: string | null;
  /** syncing: numbers not in yet · active · reconnect: Google refused, connect again. */
  status: 'syncing' | 'active' | 'reconnect';
  /** Plain words for the owner when the last sync failed. */
  error: string | null;
  connectedAt: string;
  syncedAt: string | null;
}

export type SourceKey = 'search' | 'direct' | 'social' | 'email' | 'referral' | 'other';

/** One day of the chart: visitors, split into search, social and everything else. */
export interface AnalyticsDay {
  day: string;
  visitors: number;
  pageviews: number;
  search: number;
  social: number;
  other: number;
}

export interface AnalyticsTopPage {
  path: string;
  views: number;
  visitors: number;
  topSource: SourceKey | null;
  topShare: number | null;
  /** The TinyCMS article or page at that address, when it is one. */
  kind: 'article' | 'page' | null;
  id: string | null;
  title: string | null;
}

/**
 * Website numbers from Google Analytics, as the engine last synced them.
 * ranges is null until the first sync lands.
 */
export interface WebsiteAnalytics {
  connection: AnalyticsConnection | null;
  /** A G- ID is on the site but Google Analytics isn't connected. */
  tagOnly: boolean;
  ga4Id: string | null;
  ranges:
    | ({
        from: string;
        to: string;
        visitors: WeekCount;
        sessions: WeekCount;
        pageviews: WeekCount;
      } & Record<SourceKey, WeekCount>)
    | null;
  sources: Array<{ key: SourceKey; label: string; count: number; share: number }>;
  daily: AnalyticsDay[];
  previousDaily: AnalyticsDay[];
  topPages: AnalyticsTopPage[];
}

/**
 * Search numbers from Google Search Console, as the engine last synced them
 * (apiserver searchAnalytics.js). `live` is false until the first sync; spans
 * end on `throughDay`, the newest day Google had numbers for (2–3 days ago).
 */
export interface SearchTop {
  rank: number;
  clicks: number;
  impressions: number;
  position: number | null;
}
export interface SearchAnalytics {
  live: boolean;
  throughDay: string | null;
  updatedAt: string | null;
  state: 'ok' | 'failed' | null;
  error: string | null;
  ranges: {
    clicks: WeekCount;
    impressions: WeekCount;
    ctr: { value: number | null; previous: number | null };
    position: { value: number | null; previous: number | null };
  } | null;
  daily: Array<{ day: string; clicks: number; impressions: number; position: number | null }>;
  previousDaily: Array<{
    day: string;
    clicks: number;
    impressions: number;
    position: number | null;
  }>;
  topPages: Array<
    SearchTop & {
      path: string;
      kind: 'page' | 'article' | null;
      id: string | null;
      title: string | null;
    }
  >;
  topQueries: Array<SearchTop & { query: string }>;
  /** Today only: what Google itself reports as wrong (google_* site-check findings), at most 3. */
  issues?: Array<{ severity: 'error' | 'warning'; code: string; url: string; message: string }>;
}

/** Today → Performance: the site's last 7 days. */
export interface TodayPerformance {
  /** The span counted (Today: 7; Analytics: 7, 28 or 90). */
  days?: number;
  from: string;
  to: string;
  formResponses: WeekCount;
  readerQuestions: WeekCount;
  /** Absent from an older API. */
  website?: WebsiteAnalytics;
  /** Google Search Console numbers; absent from an older API. */
  search?: SearchAnalytics;
}

export interface TodayAgent {
  at: string;
  group: 'article' | 'page' | 'site' | 'form' | 'media' | 'social' | 'campaign' | 'facts';
  count: number;
}

/** A dataset changed since it went live (or never published), waiting on a yes. */
export interface TodayDataset {
  kind: 'dataset';
  /** The article (topic) it belongs to. */
  id: string;
  /** The article's title. */
  title: string | null;
  name: string | null;
  recordCount: number | null;
  live: boolean;
  updatedAt: string | null;
  workingHash: string;
  checks: TodayChecks | null;
}

/** An agent's Ask first request (confirm_agent_action), waiting for the person. */
export interface TodayAgentRequest {
  id: string;
  /** code: a page's own header/footer code (set_page_code); only an admin approves it. */
  kind: 'drafts' | 'surface' | 'delete' | 'code';
  /** "create or edit content", "delete content" … */
  action: string;
  /** The change, in the agent's words. */
  what: string;
  agentName: string;
  requestedAt: string;
  expiresAt: string;
}

export interface TodayData {
  from: string;
  to: string;
  waiting: TodayWaiting[];
  groups?: TodayGroup[];
  /** Datasets waiting on a yes (absent from an older API). */
  datasets?: TodayDataset[];
  /** Agent Ask first requests (absent from an older API). */
  agentRequests?: TodayAgentRequest[];
  scheduled: TodayScheduled[];
  approvals: TodayApproval[];
  agent: TodayAgent[];
  /** Other edits in the window (the app, Prism AI, campaigns), in agent's shape. */
  edits?: TodayAgent[];
  responses?: TodayResponses[];
  /** Menu links readers find empty or missing on the live site (null before a first publish). */
  menuLinks?: TodayMenuLinks | null;
  /** The last 7 days against the 7 before, whatever the window (missing before the API has it). */
  performance?: TodayPerformance;
}

export interface CampaignPost {
  id: string;
  title?: string | null;
  status: string;
  scheduledDate?: string | null;
  targets: Array<{ id: string; platform: string }>;
  voiceCheck?: VoiceCheck | null;
  itemCheck?: ItemCheck | null;
}
export interface TodayCampaign {
  id: string;
  name: string;
  kind: 'campaign' | 'always_on';
  status: string;
  startDate?: string | null;
  deadline?: string | null;
  landingPageUrl?: string | null;
  /** The campaign's color key (campaignColors.ts); null → spread by id. */
  color?: string | null;
  createdAt: string;
  posts: CampaignPost[];
}

const TODAY_CAMPAIGNS = gql`
  query TodayCampaigns($accountId: ID!) {
    projects(accountId: $accountId) {
      id
      name
      kind
      status
      startDate
      deadline
      landingPageUrl
      color
      createdAt
      posts {
        id
        title
        status
        scheduledDate
        voiceCheck
        itemCheck
        targets {
          id
          platform
        }
      }
    }
  }
`;

/** Campaigns sent back with Ask for changes and not changed since (apiserver reach). */
const CAMPAIGNS_SENT_BACK = gql`
  query CampaignsSentBack($accountId: ID!) {
    campaignsSentBack(accountId: $accountId)
  }
`;

/**
 * How often the Approvals review's count refreshes while the app is open: an
 * agent's request shows up in the top bar without a click (60 s, as the
 * Content badge's form responses).
 */
export const APPROVALS_POLL_MS = 60_000;

export type Span = 'day' | 'week';

/** Local midnight today → tomorrow (day), or the six days before today too (week). */
export function todayWindow(span: Span, now = new Date()) {
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  if (span === 'week') start.setDate(start.getDate() - 6);
  const end = new Date(now);
  end.setHours(0, 0, 0, 0);
  end.setDate(end.getDate() + 1);
  return { from: start.toISOString(), to: end.toISOString() };
}

/**
 * The Reach decisions Today counts: posts that didn't go out in the last seven
 * days, posts waiting for approval, and campaigns waiting on a yes. Today's
 * "need you" and Reach's "N things need you" both read it, so the two counts
 * can't disagree (QA round 12: Reach said 1 while Today said 3).
 */
export function useReachDecisions({ skip = false, poll = false } = {}) {
  const { accountId } = useAccount();
  const { reachOn } = useAddons();
  // Keyed by the calendar day so a page left open rolls over at midnight on refetch.
  const dayKey = new Date().toDateString();
  // Reach is asked for the week, so a post that didn't go out late yesterday
  // still waits. What went out is cut to Today's own window by its page.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const reachWin = useMemo(() => todayWindow('week'), [dayKey]);
  const pollInterval = poll ? APPROVALS_POLL_MS : undefined;
  const reach = useQuery<{ reachCalendar: { posts: ReachPost[]; waiting: ReachPost[] } }>(
    REACH_CALENDAR,
    {
      variables: { accountId, ...reachWin },
      skip: skip || !accountId || !reachOn,
      fetchPolicy: 'cache-and-network',
      pollInterval,
    }
  );
  const campaignsQ = useQuery<{ projects: TodayCampaign[] }>(TODAY_CAMPAIGNS, {
    variables: { accountId },
    skip: skip || !accountId || !reachOn,
    fetchPolicy: 'cache-and-network',
    pollInterval,
  });
  // Sent back with Ask for changes: waiting on the agent, not the person.
  // An older API without the query leaves the list empty (errorPolicy).
  const sentBackQ = useQuery<{ campaignsSentBack: string[] }>(CAMPAIGNS_SENT_BACK, {
    variables: { accountId },
    skip: skip || !accountId || !reachOn,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    pollInterval,
  });

  const waitingPosts = useMemo(() => reach.data?.reachCalendar.waiting || [], [reach.data]);
  const posts = useMemo(() => reach.data?.reachCalendar.posts || [], [reach.data]);
  // Posts whose time passed without going out (Try again / Skip on Today).
  const missedPosts = useMemo(
    () =>
      posts
        .filter((p) => p.reachStatus === 'missed')
        .sort((a, b) => (postTime(b)?.getTime() || 0) - (postTime(a)?.getTime() || 0)),
    [posts]
  );
  // Dated campaigns with tactics in review, and always-on campaigns an agent set
  // up that the person hasn't turned on yet — unless sent back for changes.
  const campaigns = useMemo(() => {
    const sentBack = new Set(sentBackQ.data?.campaignsSentBack || []);
    return (campaignsQ.data?.projects || []).filter((c) =>
      sentBack.has(c.id)
        ? false
        : c.kind === 'always_on'
          ? c.status === 'planning'
          : // A finished campaign (incl. one dismissed from Today) is off Today.
            c.status !== 'paused' &&
            c.status !== 'completed' &&
            c.posts.some((p) => p.status === 'in_review')
    );
  }, [campaignsQ.data, sentBackQ.data]);
  // Every campaign, for Today's timeline (finished ones are dropped there).
  const allCampaigns = useMemo(() => campaignsQ.data?.projects || [], [campaignsQ.data]);
  return {
    reachOn,
    reach,
    campaignsQ,
    sentBackQ,
    posts,
    waitingPosts,
    missedPosts,
    campaigns,
    allCampaigns,
    // Posts that belong to a campaign in review are approved with the campaign.
    count: missedPosts.length + waitingPosts.length + campaigns.length,
  };
}

export function useToday(span: Span = 'day', { skip = false, poll = false } = {}) {
  const { accountId } = useAccount();
  // Keyed by the calendar day so a page left open rolls over at midnight on refetch.
  const dayKey = new Date().toDateString();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const win = useMemo(() => todayWindow(span), [span, dayKey]);

  const today = useQuery<{ tinycmsToday: TodayData }>(GET_TINYCMS_TODAY, {
    variables: { accountId, ...win },
    skip: skip || !accountId,
    fetchPolicy: 'cache-and-network',
    pollInterval: poll ? APPROVALS_POLL_MS : undefined,
  });
  const {
    reachOn,
    reach,
    campaignsQ,
    sentBackQ,
    posts,
    waitingPosts,
    missedPosts,
    campaigns,
    allCampaigns,
    count,
  } = useReachDecisions({ skip, poll });

  const data = today.data?.tinycmsToday || null;
  // Everything the Approvals review lists (support access aside: owner only, its own query).
  const need =
    (data?.waiting.length || 0) +
    (data?.datasets?.length || 0) +
    (data?.agentRequests?.length || 0) +
    count;

  const refetch = () =>
    Promise.all([
      today.refetch(),
      reachOn ? reach.refetch() : null,
      reachOn ? campaignsQ.refetch() : null,
      reachOn ? sentBackQ.refetch() : null,
    ]);

  // Not known yet, not empty: the account is still resolving (the queries are
  // skipped until it is) or a first fetch is out. Sections show bars until then.
  const pending = (q: { data?: unknown; loading: boolean; error?: unknown }, on = true) =>
    !skip && on && !q.data && !q.error && (q.loading || !accountId);

  return {
    window: win,
    loading: pending(today),
    // Reach posts and campaigns, which fill the queue and Went out too.
    reachLoading: pending(reach, reachOn) || pending(campaignsQ, reachOn),
    error: today.error,
    data,
    waitingPosts,
    missedPosts,
    posts,
    campaigns,
    allCampaigns,
    need,
    reachOn,
    refetch,
  };
}
